import { gql } from '@apollo/client';
import IconEdit from '@apollo/icons/default/IconEdit.svg';
import IconListBullet from '@apollo/icons/default/IconListBullet.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  Spinner,
  Text,
} from '@apollo/orbit';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { WrapMaybe } from 'src/components/wrapMaybe/WrapMaybe';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';
import { InternalIdentity, Service, User } from 'src/lib/graphqlTypes/types';
import { setOverlayVisibleLocation } from 'src/lib/routing';
import { LinkableHeading } from 'src/lib/safeCompileMarkdown/SafeMarkdown';
import { scrollTo } from 'src/lib/scrollTo';

import { GraphRef } from '../../hooks/useGraphRef';
import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';
import { ReadmeMarkdown } from '../readmeMarkdown/ReadmeMarkdown';

export const readmeCardReadmeFragment = gql`
  fragment ReadmeCardReadmeFragment on Readme {
    id
    content
    lastUpdatedTime
    lastUpdatedBy {
      id
      ... on User {
        fullName
      }
      ... on Service {
        id
        title
      }
      ... on InternalIdentity {
        name
      }
    }
  }
`;

type Readme = {
  __typename: 'Readme';
  content: string;
  id: string;
  lastUpdatedTime: GraphQLTypes.Maybe<Timestamp['output']>;
  lastUpdatedBy: GraphQLTypes.Maybe<
    | ({ __typename: 'InternalIdentity' } & Pick<
        InternalIdentity,
        'name' | 'id'
      >)
    | ({ __typename: 'Service' } & Pick<Service, 'id' | 'title'>)
    | ({ __typename: 'User' } & Pick<User, 'fullName' | 'id'>)
  >;
};

export const DEFAULT_README_ID = 'a15177c0-b003-4837-952a-dbfe76062eb1';
export const ReadmeCard = ({
  graphRef,
  readme,
  shortcodesData,
  loading,
  className,
  canEditProposal,
}: {
  graphRef: GraphRef;
  readme: Readme;
  shortcodesData?: GraphQLTypes.ReadmeMarkdownServiceFragment | null;
  loading: boolean;
  className?: string;
  canEditProposal: boolean;
}) => {
  const { graphId, graphVariant } = graphRef;

  const readmeContent = readme.content;
  const graphTitle = shortcodesData?.title;
  const variantUrl = shortcodesData?.variant?.url;
  const location = useLocation();

  const isDefaultReadme = readme.id === DEFAULT_README_ID;

  const { isProposalRoute } = useInternalGraphLinking();

  const [headings, setHeadings] = React.useState<LinkableHeading[]>([]);
  const lowestHeaderType = Math.min(
    ...headings.map((heading) => heading.headingLevel),
  );

  const { hash } = useLocation();
  React.useEffect(() => {
    if (hash) {
      scrollTo(hash);
    }
  }, [hash]);
  const titleOfCard = isProposalRoute ? 'Description' : 'README';

  return (
    <Card isDivided className={className}>
      <CardHeader>
        <CardTitle>{titleOfCard}</CardTitle>
        {!isDefaultReadme && readme.lastUpdatedTime && (
          <CardDescription>
            Updated{' '}
            <Text as="span" variant="bodySb">
              <RelativeTimestamp
                format="fromnow"
                value={readme.lastUpdatedTime}
              />
            </Text>{' '}
            <WrapMaybe
              element={
                <PermissionGuard
                  graphId={graphId}
                  graphPermissions="canQueryReadmeAuthor"
                  fallback={null}
                />
              }
              on={!isProposalRoute}
            >
              <Tooltip
                placement="top"
                label="This user is deleted. Please check the audit log."
                disabled={!!readme.lastUpdatedBy}
              >
                <span>
                  by{' '}
                  {readme.lastUpdatedBy?.__typename === 'User' &&
                  readme.lastUpdatedBy.fullName
                    ? `${readme.lastUpdatedBy.fullName}`
                    : readme.lastUpdatedBy?.__typename === 'Service'
                      ? `${readme.lastUpdatedBy.title}`
                      : readme.lastUpdatedBy?.__typename === 'InternalIdentity'
                        ? `${readme.lastUpdatedBy.name}`
                        : 'Unknown'}
                </span>
              </Tooltip>
            </WrapMaybe>
          </CardDescription>
        )}
        <ButtonGroup>
          {headings.length >= 2 && (
            <Menu>
              <MenuButton
                as={Button}
                leftIcon={<IconListBullet />}
                variant="secondary"
              >
                Outline
              </MenuButton>
              <MenuContent>
                <MenuList>
                  {headings.map((heading, index) => (
                    <MenuItem
                      key={heading.id}
                      as={Link}
                      to={{ ...location, hash: heading.id }}
                      style={{
                        marginLeft: `${
                          index === 0
                            ? 0
                            : ((heading.headingLevel ?? 1) - lowestHeaderType) *
                              8
                        }px`,
                      }}
                    >
                      {heading.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </MenuContent>
            </Menu>
          )}

          <WrapMaybe
            on={!isProposalRoute}
            element={
              <PermissionGuard
                graphId={graphId}
                graphVariant={graphVariant}
                graphVariantPermissions="canUpdateVariantReadme"
                fallback={null}
              />
            }
          >
            <Tooltip placement="top" label={`Edit ${titleOfCard}`}>
              <Button
                role="button"
                aria-label={`Edit ${titleOfCard}`}
                variant="secondary"
                data-analytics-label="README: Edit README"
                data-analytics-category="Variant Home"
                as={Link}
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.editGraphReadme,
                })}
                leftIcon={<IconEdit />}
              >
                Edit
              </Button>
            </Tooltip>
          </WrapMaybe>
        </ButtonGroup>
      </CardHeader>
      <CardBody>
        {loading ? (
          <Spinner
            className="m-auto mt-4 items-center"
            size="lg"
            data-testid="spinner"
          />
        ) : (
          <>
            {(!isProposalRoute || canEditProposal) && (
              <PermissionGuard
                graphId={graphId}
                graphVariant={graphVariant}
                graphVariantPermissions="canUpdateVariantReadme"
                fallback={null}
              >
                {isDefaultReadme && (
                  <Alert status="info">
                    This is the default README for this variant. You can
                    customize it to help your API developers get the most out of
                    your API.
                  </Alert>
                )}
              </PermissionGuard>
            )}
            <ReadmeMarkdown
              content={readmeContent}
              graphRef={graphRef}
              graphTitle={graphTitle}
              variantUrl={variantUrl}
              setHeadings={setHeadings}
            />
          </>
        )}
      </CardBody>
    </Card>
  );
};
